import { Outlet, useNavigate } from "react-router";
import { Suspense, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { Separator } from "@/components/ui/separator";
import { GlobalSearch } from "@/components/global-search";
import { CommandBarMount } from "@/components/command-bar";
import { Skeleton } from "@/components/ui/skeleton";

// Authenticated shell. Mirrors `web/src/app/(app)/layout.tsx` from the
// Next app one-to-one. The big difference is the auth guard: in Next,
// `src/proxy.ts` runs at the edge and redirects unauthenticated
// requests; in the SPA, we redirect client-side once Clerk finishes
// loading.
export function AppLayout() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate("/sign-in", { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate]);

  if (!isLoaded || !isSignedIn) {
    // Render nothing during the brief window before Clerk hydrates.
    // Returning the shell would briefly leak protected UI to anyone
    // who deep-linked while signed out.
    return null;
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "350px",
        } as React.CSSProperties
      }
    >
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <GlobalSearch />
        </header>
        <main className="flex-1 overflow-auto p-6">
          {/* Suspense catches each lazy(() => import(...)) route while
              its chunk streams in. The chrome above stays painted so
              navigations feel instant — only the inner content shows
              a skeleton. See main.tsx for the lazy() chunking. */}
          <Suspense fallback={<RouteSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </SidebarInset>
      <CommandBarMount />
    </SidebarProvider>
  );
}

function RouteSkeleton() {
  // Minimal placeholder — most pages render their own Skeleton block
  // for the data load. This one just covers the gap between Suspense
  // firing and the route module's top-level render.
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
